import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, ClipboardCheck, Layers, Users } from 'lucide-react';
import { StatsCard, StatsGrid } from '../../../ui';
import { attendanceTone, plural } from '../trainerDashboardUtils';

/**
 * TrainerVitals — the four numbers a trainer reads first: how many students,
 * how many batches, whether those students are turning up, and how much
 * marking is waiting.
 *
 * A tile's icon is coloured only when its number needs attention — four hues in
 * a row say nothing, and then the amber one says nothing too.
 *
 * Props:
 *   stats     — { studentsMentored, activeBatches, activeCourses, totalBatches }
 *   analytics — { studentsThisMonth, attendanceRate }
 *   trend     — [{ month, pct }], months with classes only
 *   pending   — submissions waiting for review
 */
const TrainerVitals = ({ stats = {}, analytics = {}, trend = [], pending = 0 }) => {
  const navigate = useNavigate();
  const rate = analytics.attendanceRate ?? null;
  const joined = analytics.studentsThisMonth || 0;
  const activeBatches = stats.activeBatches || 0;
  const activeCourses = stats.activeCourses || 0;

  return (
    <StatsGrid cols={4}>
      <StatsCard
        title="Students"
        icon={Users}
        iconColor="primary"
        value={stats.studentsMentored || 0}
        subtitle={joined > 0
          ? `+${joined} joined this month`
          : `Across ${plural(activeBatches, 'active batch', 'active batches')}`}
      />
      <StatsCard
        title="Active batches"
        icon={Layers}
        iconColor="primary"
        value={activeBatches}
        subtitle={`${plural(activeCourses, 'course', 'courses')} · ${plural(stats.totalBatches || 0, 'batch', 'batches')} in total`}
      />
      <StatsCard
        title="Attendance"
        icon={CalendarCheck}
        iconColor={attendanceTone(rate) === 'warning' ? 'amber' : 'primary'}
        value={rate === null ? '—' : `${rate}%`}
        series={trend.map((t) => t.pct)}
        subtitle={rate === null ? 'No classes held yet' : 'Last 5 months'}
      />
      <StatsCard
        title="To review"
        icon={ClipboardCheck}
        iconColor={pending > 0 ? 'amber' : 'primary'}
        value={pending}
        subtitle={pending > 0 ? 'Submissions waiting for you' : 'All caught up'}
        onClick={() => navigate('/i/batch-assignments')}
      />
    </StatsGrid>
  );
};

export default TrainerVitals;
